/**
 * The pause at the start of a round. scope.md §3.4.
 *
 * Nobody plays until everybody has said they are ready, which gives each player time
 * to read what this round wants, look at a worked example, and sort their hand.
 */

import { useState } from 'react';
import type { Card, PlayerView } from '@garbage/shared';
import { ROUNDS } from '@garbage/shared';
import { contractFor, exampleFor } from '@garbage/engine';
import { Rich, useContractText, useT } from '../i18n.js';
import { SettingsButton } from './SettingsSheet.js';
import { displayHand } from '../handOrder.js';
import { CardFace } from './Card.js';
import type { PackId } from '../packs.js';
import { Hand } from './Hand.js';

export function RoundBriefing({
  view, customOrder, pack, onReorder, onReady, onHelp, onSettings,
}: {
  view: PlayerView;
  customOrder: string[] | null;
  pack: PackId;
  onReorder: (order: string[] | null) => void;
  onReady: () => void;
  onHelp: () => void;
  onSettings?: () => void;
}) {
  const t = useT();
  const contractText = useContractText();
  const [picked, setPicked] = useState<string | null>(null);
  const self = view.players.find((p) => p.id === view.you);
  const hand = displayHand(self?.hand ?? [], customOrder);
  const contract = contractFor(view.round, view.rules);
  const example = exampleFor(contract, (self?.hand?.length ?? 0) + 1);

  // Whoever leads is already in the turn: round 1's is drawn at random, then it moves
  // one seat each round. Said up front so nobody is caught out when play starts. §3.1.
  const lead = view.players.find((p) => p.id === view.turn.playerId);
  const youLead = lead?.id === view.you;

  const youAreReady = self?.ready ?? false;
  const waitingFor = view.players.filter((p) => !p.ready && !p.bot && p.connected);

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-start gap-2 px-4 pt-[calc(1rem+var(--pad-top))]">
        <div className="flex-1">
          <p className="text-[11px] font-bold uppercase tracking-widest text-bone/50">
            {t('briefing.header', { round: view.round, rounds: ROUNDS, cards: self?.hand?.length ?? 0 })}
          </p>
          <h1 className="text-2xl font-black leading-tight text-sun" data-testid="briefing-contract">
            {t('briefing.youNeed', { contract: contractText(contract) })}
          </h1>
        </div>
        <button
          type="button"
          data-testid="help-button"
          onClick={onHelp}
          aria-label={t('common.howToPlay')}
          className="rounded-full bg-white/10 px-3 text-lg font-black"
        >
          ?
        </button>
        {onSettings && <SettingsButton onClick={onSettings} />}
      </header>

      {lead && (
        <p className="px-4 pt-3" data-testid="briefing-lead">
          <span className="inline-block rounded-full bg-sun/20 px-3 py-1 text-sm font-black text-sun ring-2 ring-sun">
            {youLead ? t('briefing.youLead') : t('briefing.leads', { name: lead.name })}
          </span>
        </p>
      )}

      {/* A worked example, because the words mean little on their own. */}
      <section className="px-4 pt-3" data-testid="briefing-example">
        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-widest text-bone/40">
          {t('briefing.example')}
        </p>
        <div className="flex flex-wrap gap-3 rounded-xl bg-black/25 p-3">
          {example.map((meld, i) => (
            <div key={i} className="flex flex-col gap-1">
              <div className="flex gap-0.5">
                {meld.cards.map((c, n) => (
                  <CardFace
                    key={n}
                    card={exampleCard(c, `ex-${i}-${n}`)}
                    size={meld.cards.length > 5 ? 'tiny' : 'small'}
                    pack={pack}
                  />
                ))}
              </div>
              <span className="text-[10px] text-bone/50">
                {meld.kind === 'set' ? t('meld.set.explained') : t('meld.run.explained')}
              </span>
            </div>
          ))}
        </div>
        {contract.useWholeHand && (
          <p className="mt-2 text-xs text-bone/60">
            <Rich text={t('briefing.lastRound')} />
          </p>
        )}
      </section>

      {/* The card turned up at the deal. Visible now, and up for grabs the moment
          everybody is ready — so it is worth taking into account while you sort. */}
      {view.discardTop && (
        <section className="flex items-center gap-3 px-4 pt-4" data-testid="briefing-upcard">
          <CardFace card={view.discardTop} size="medium" pack={pack} />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-bone/40">
              {t('briefing.turnedUp')}
            </p>
            <p className="text-xs text-bone/70">
              {t('briefing.turnedUpHint')}
            </p>
          </div>
        </section>
      )}

      <p className="px-4 pt-4 text-[11px] font-bold uppercase tracking-widest text-bone/40">
        {t('briefing.yourCards')}
      </p>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <Hand
          cards={hand}
          pack={pack}
          onReorder={onReorder}
          onSelect={(card) => setPicked(picked === card.id ? null : card.id)}
          selectedId={picked}
          canAct={false}
        />
      </div>

      <div className="px-4 pb-[calc(1rem+var(--pad-bottom))]">
        {!youAreReady ? (
          <button
            type="button"
            data-testid="ready-button"
            onClick={onReady}
            className="w-full rounded-2xl bg-sun py-4 text-xl font-black text-ink active:scale-95"
          >
            {t('briefing.ready')}
          </button>
        ) : (
          <p data-testid="waiting-for" className="py-4 text-center text-sm text-bone/60">
            {waitingFor.length > 0
              ? t('common.waitingFor', { names: waitingFor.map((p) => p.name).join(', ') })
              : t('briefing.starting')}
          </p>
        )}
        <ul className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1" data-testid="ready-list">
          {view.players.map((player) => (
            <li
              key={player.id}
              data-testid={`ready-${player.id}`}
              className={`text-xs ${player.ready || player.bot ? 'text-sun' : 'text-bone/35'} ${
                player.id === lead?.id ? 'rounded-full px-1.5 font-black ring-1 ring-sun' : ''}`}
            >
              {player.ready || player.bot ? '✓' : '○'} {player.name}
              {player.id === lead?.id && <span className="sr-only"> {t('briefing.leadsTag')}</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** The example is illustration, not real cards, so it gets its own ids. */
function exampleCard(spec: { rank: Card['rank']; suit: Card['suit'] }, id: string): Card {
  return { id, rank: spec.rank, ...(spec.suit ? { suit: spec.suit } : {}) };
}
